import { DateTime, Interval } from 'luxon';
import Chart, { ChartConfiguration, ChartDataSets } from 'chart.js';
import { COLORS, LINE_CHART_COLORS } from '../../../config/constants';
import { genArray, simulateRandomIncreasingData, withMonth } from '../../../utils/functions';
import React, { useContext, useMemo } from 'react';
import { BetterChartComponent } from '../../general/ChartComponent';
import { toPrice } from '../../../utils/parsers';
import chartDataLabels from 'chartjs-plugin-datalabels';
import { ForecastContext } from '../../../contexts/investment-robot/ForecastContext';
import { PeriodicInterval } from '../../../api/robotapi';
import { useWindowBreakpoints } from '../../../hooks/useWindowBreakpoints';

Chart.defaults.global.defaultFontFamily = 'museo-sans, sans-serif';
Chart.defaults.global.defaultFontColor = COLORS.bndGrey;
Chart.defaults.global.defaultFontSize = 16;

// Source line style: https://codepen.io/pravileaf/pen/OqyXRK
// Source extension: https://stackoverflow.com/questions/54990176/chart-js-vertical-line-when-hovering-and-shadow-on-line
let draw = Chart.controllers.line.prototype.draw;
Chart.controllers.line = Chart.controllers.line.extend({
    draw: function () {
        if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
            var activePoint = this.chart.tooltip._active[0],
                ctx = this.chart.ctx as CanvasRenderingContext2D,
                x = activePoint.tooltipPosition().x,
                topY = this.chart.scales['y-axis-0'].top,
                bottomY = this.chart.scales['y-axis-0'].bottom;

            // draw line
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x, topY);
            ctx.lineTo(x, bottomY);
            ctx.setLineDash([10, 10]);
            ctx.strokeStyle = '#bbb';
            ctx.stroke();
            ctx.restore();
        }
        draw.apply(this, arguments);
    },
});

export type ChartData = {
    id: string;
    label: string;
    data: Data[];
};

/**
 * @link t = timestamp in milliseconds
 * @link y = a datavalue
 */
type Data = { t: number; y: number };

export const randomChartData = (numDays: number, endDate: DateTime) =>
    genArray<number>(numDays, simulateRandomIncreasingData()).map((number, index, array) =>
        withMonth(number, index, array, endDate),
    );

const numLabels = 5;

const chartOptions = (isMobile: boolean): ChartConfiguration['options'] => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        // aspectRatio: 2,
        legend: {
            display: false,
        },
        elements: {
            line: {
                tension: 0,
                fill: false,
            },
            point: {
                radius: 0,
                hoverRadius: 4,
            },
        },
        tooltips: {
            enabled: false,

            custom: function (tooltipModel) {
                // Tooltip Element
                var shadowRoot = document.getElementsByTagName('investment-robot')[0]?.shadowRoot;
                const webComponentElement = shadowRoot?.querySelector('.investment-robot');

                var tooltipEl = shadowRoot?.getElementById('chartjs-tooltip');

                // Create element on first render
                if (!tooltipEl) {
                    tooltipEl = document.createElement('div');
                    tooltipEl.id = 'chartjs-tooltip';
                    tooltipEl.innerHTML = '<table></table>';
                    // shadowRoot?.appendChild(tooltipEl);
                    shadowRoot?.querySelector('.investment-robot')?.appendChild(tooltipEl);
                }

                // Hide if no tooltip
                if (tooltipModel.opacity === 0) {
                    tooltipEl.style.opacity = 0 + '';
                    return;
                }

                // Set caret Position
                tooltipEl.classList.remove('above', 'below', 'no-transform');
                if (tooltipModel.yAlign) {
                    tooltipEl.classList.add(tooltipModel.yAlign);
                } else {
                    tooltipEl.classList.add('no-transform');
                }

                function getBody(bodyItem: any) {
                    return bodyItem.lines;
                }

                // Set Text
                if (tooltipModel.body) {
                    var titleLines = tooltipModel.title || [];
                    var bodyLines = tooltipModel.body.map(getBody);

                    var innerHtml = '<thead>';

                    titleLines.forEach(function (title) {
                        innerHtml +=
                            '<tr><th style="text-align:left; font-size: 18px; padding-bottom: 10px;">' +
                            title +
                            '</th></tr>';
                    });
                    innerHtml += '</thead><tbody>';

                    bodyLines.forEach(function (body, index) {
                        const label = (body[0] as string).split(': €')[0];
                        const amount = '€' + (body[0] as string).split(': €')[1];
                        var color = index === 0 ? COLORS.bndPink : LINE_CHART_COLORS[index - 1].legend;
                        var style = 'background:' + color;
                        style += '; width: 20px';
                        style += '; height: 20px';
                        style += '; border-radius: 5px';
                        style += '; margin-right: 10px';
                        var span = '<div style="' + style + '"></div>';
                        innerHtml +=
                            '<tr><td style="display: flex; align-items: center; flex-direction: row;">' +
                            span +
                            "<span style='flex: 1'>" +
                            label +
                            '</span></td><td style="padding-left: 15px;">' +
                            amount +
                            '</td></tr>';
                    });
                    innerHtml += '</tbody>';

                    var tableRoot = tooltipEl.querySelector('table');
                    tableRoot!.innerHTML = innerHtml;
                }

                // `this` will be the overall tooltip
                var position = (this as any)._chart.canvas.getBoundingClientRect();

                // Display, position, and set styles for font
                tooltipEl.style.opacity = 1 + '';
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.background = 'rgba(30,30,30,0.8)';
                tooltipEl.style.borderRadius = '10px';
                tooltipEl.style.color = '#fff';
                let positionLeft = position.left + window.pageXOffset + tooltipModel.caretX;
                if (positionLeft > window.innerWidth / 2) {
                    positionLeft -= tooltipEl.offsetWidth;
                }
                tooltipEl.style.left = positionLeft + 'px';
                tooltipEl.style.top =
                    position.top +
                    // window.pageYOffset +
                    tooltipModel.caretY -
                    (webComponentElement?.getBoundingClientRect().top || 0) +
                    'px';
                // Original: tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                // tooltipEl.style.top = position.top + tooltipModel.caretY + 'px';
                tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
                tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
                tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
                tooltipEl.style.pointerEvents = 'none';
            },

            mode: 'index',
            intersect: false,
            axis: 'x',
            xPadding: 10,
            yPadding: 10,
            bodySpacing: 10,
            bodyAlign: 'left',
            callbacks: {
                title: function (tooltip, data) {
                    const datapoint = data?.datasets?.[0]?.data?.[tooltip[0]?.index || 0] as { x?: number; t?: number };

                    if (datapoint.t) {
                        return DateTime.fromMillis(datapoint.t).setLocale('nl').toLocaleString(DateTime.DATE_FULL);
                    }

                    return 'Indexwaarde';
                },
                label: function (tooltip, data) {
                    if (!tooltip.value) return `${tooltip.label}: onbekend`;

                    const _value = +tooltip.value;
                    const label = data.datasets?.[tooltip.datasetIndex!].label;

                    return `${label}: ${toPrice(_value, { onlyFullEuros: true, withCurrencySymbol: true })}`;
                },
                // labelColor: function (tooltipItem, chart) {
                //     const index = tooltipItem.datasetIndex || 0;

                //     return {
                //         borderColor: index === 0 ? COLORS.bndPink : LINE_CHART_COLORS[index - 2],
                //         backgroundColor: index === 0 ? COLORS.bndPink : LINE_CHART_COLORS[index - 2],
                //     };
                // },
            },

            filter: function (tooltipItem) {
                return tooltipItem.datasetIndex !== 1;
            },
        },
        hover: {
            // disable animations due to https://github.com/chartjs/Chart.js/issues/7266
            animationDuration: 0,
            mode: 'index',
            intersect: false,
            axis: 'x',
        },
        scales: {
            yAxes: [
                {
                    position: 'right',
                    gridLines: {
                        drawBorder: false,
                        drawTicks: true,
                        lineWidth: 1.2,
                        zeroLineWidth: 1.2,
                        color: 'rgba(36, 36, 36, 0.2)',
                        zeroLineColor: 'rgba(36, 36, 36, 0.2)',
                    },
                    ticks: {
                        padding: 8,
                        callback: function (value, index, values) {
                            return isMobile
                                ? `${toPrice(+value / 1000, { onlyFullEuros: true, withCurrencySymbol: true })}${
                                      +value > 1000 ? 'k' : ''
                                  }`
                                : toPrice(+value, { onlyFullEuros: true, withCurrencySymbol: true });
                        },
                    },
                },
            ],
            xAxes: [
                {
                    distribution: 'linear',

                    type: 'time',
                    bounds: 'data',

                    gridLines: {
                        display: false,
                        drawBorder: true,
                    },
                    ticks: {
                        source: 'labels',
                        minRotation: 0,
                        maxRotation: 45,
                    },
                    time: {
                        displayFormats: {
                            second: 'HH:mm:ss',
                            day: 'D MMM',
                            week: 'D MMM YYYY',
                        },
                    },
                },
            ],
        },
    };
};

export const ForecastLineChart: React.FC<{
    chartData: ChartData[];
}> = ({ chartData }) => {
    const { forecast } = useContext(ForecastContext);
    const current = DateTime.now().startOf('day');
    const interval = Interval.after(current, { years: forecast.parameters.capitalGoal.inYears });

    const { isMobile } = useWindowBreakpoints();

    const labels = useMemo(() => {
        return generateLabels(interval);
    }, [forecast.parameters.capitalGoal.inYears]); // eslint-disable-line react-hooks/exhaustive-deps

    const datasets = useMemo(() => {
        const investmentLine = generateInvestmentLine(
            interval.count('months'),
            forecast.parameters.deposits.periodic.amount,
            forecast.parameters.deposits.periodic.interval,
            forecast.parameters.deposits.initial.amount,
        );
        const goalLine = generateGoalLine(interval.count('months'), forecast.parameters.capitalGoal.amount);
        const realDataSets = chartData.map((data, i) => {
            return toDataSet(data, i);
        });
        return [investmentLine, goalLine, ...realDataSets];
    }, [chartData, forecast.parameters]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="relative w-full h-full">
            <BetterChartComponent
                type="line"
                data={{ datasets, labels }}
                options={chartOptions(isMobile)}
                plugins={[chartDataLabels]}
            />
            <div id="line-chart-tooltip" className="absolute"></div>
        </div>
    );
};

function toDataSet(chartData: ChartData, index: number): ChartDataSets {
    // cycle fund colors
    let color = LINE_CHART_COLORS[index % 3].chart;
    return {
        label: chartData.label,
        data: chartData.data,
        backgroundColor: index === 0 ? color : index === 1 ? LINE_CHART_COLORS[index - (1 % 3)].chart : color,
        borderColor: index === 1 ? color : 'transparent',
        fill: index === 0 ? 'false' : '-1',
        pointHoverBackgroundColor: color,
        datalabels: { display: false },
    };
}

function generateLabels(interval: Interval): number[] {
    const intervals = interval.divideEqually(numLabels);
    return intervals.map((i) => {
        const subIntervals = i.divideEqually(2);
        return subIntervals.length === 0 ? 0 : subIntervals[0]?.end?.toMillis() ?? 0;
    });
}

/** Calculates the line that shows the total investment on a date. All currencies are in cents. */
const generateInvestmentLine = (
    months: number,
    periodicDeposit: number,
    periodicInterval: PeriodicInterval,
    initialDeposit: number,
) => {
    const currentDate = DateTime.now(); //.startOf('month');

    const lineData: Data[] = [];
    lineData[0] = { t: currentDate.toMillis(), y: initialDeposit };

    for (let i = 1; i < months; i++) {
        const investedOnDate =
            lineData[i - 1].y +
            periodicAmount(
                currentDate.plus({ month: i }),
                currentDate.plus({ month: 1 }),
                periodicInterval,
                periodicDeposit,
            );

        lineData[i] = {
            t: currentDate.plus({ month: i }).toMillis(),
            y: Math.round(investedOnDate),
        };
    }

    return {
        label: 'Inleg',
        data: lineData,
        borderColor: COLORS.bndPink,
        fill: false,
        backgroundColor: COLORS.bndPink,
        pointHoverBackgroundColor: COLORS.bndPink,
        datalabels: { display: false },
    };
};

const periodicAmount = (
    currentDate: DateTime,
    startDate: DateTime,
    periodicInterval: PeriodicInterval,
    periodicDeposit: number,
) => {
    if (periodicInterval === 'monthly') {
        return periodicDeposit;
    } else {
        if (currentDate.day === startDate.day && currentDate.month === startDate.month) {
            return periodicDeposit;
        }
        return 0;
    }
};

const generateGoalLine = (months: number, goal: number): ChartDataSets => {
    const goalIsZero = goal === 0 ? true : false;

    return {
        type: 'line',
        data: [
            { t: DateTime.now().toMillis(), y: goal },
            {
                t: DateTime.now()
                    .plus({ month: months - 1 })
                    .toMillis(),
                y: goal,
            },
        ],
        fill: false,
        pointHoverRadius: 0,
        hitRadius: 0,
        showLine: !goalIsZero,
        spanGaps: true,
        borderColor: COLORS.bndGreen,
        borderDash: [8, 12],
        borderWidth: 2,
        lineTension: 0,
        datalabels: {
            align: 'right',
            offset: 1,
            anchor: 'center',
            color: COLORS.bndWhite,
            backgroundColor: COLORS.bndGreen,
            borderRadius: 6,
            padding: {
                top: 4,
                bottom: 4,
                left: 8,
                right: 8,
            },
            font: function (context) {
                return {
                    size: 14,
                    weight: 'bold',
                };
            },
            formatter: function (value, context) {
                return `Doel: ${toPrice(value.y, { onlyFullEuros: true, withCurrencySymbol: true })}`;
            },
            display: function (context) {
                return context.dataIndex === 0 && !goalIsZero;
            },
        },
    };
};
